import React from 'react';
import { motion } from 'framer-motion';
import Layout from '../components/layout/Layout';
import Button from '../components/common/Button';
import StrategicAIVideo from '../components/homepage/StrategicAIVideo';
import { 
  LightningBoltIcon, 
  UserGroupIcon, 
  ChartBarIcon, 
  SupportIcon,
} from '@heroicons/react/outline';

const HomePage: React.FC = () => {
  const fadeInUpVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: (i: number) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.08,
        duration: 0.6,
        ease: [0.16, 1, 0.3, 1], // premium custom cubic bezier easing
      },
    }),
  };
  
  const challenges = [
    {
      stepNum: "01",
      icon: <LightningBoltIcon className="h-5 w-5" />,
      title: "Limited Market Reach",
      description: "Struggling to expand your customer base and capture new geographic or digital demographics."
    },
    {
      stepNum: "02",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="h-5 w-5 text-accent">
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12m-3-2.818.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.83-1.106-2.17 0-3l.9-.675a4.893 4.893 0 0 1 3.51-.776" />
        </svg>
      ),
      title: "High Operating Costs",
      description: "Managing manual workflows that inflate overheads and drain your operating margins."
    },
    {
      stepNum: "03",
      icon: <UserGroupIcon className="h-5 w-5" />,
      title: "Skilled Labour Shortage",
      description: "Difficulty sourcing and retaining engineering and operational talents necessary for scaling."
    },
    {
      stepNum: "04",
      icon: <ChartBarIcon className="h-5 w-5" />,
      title: "Ineffective Cash Flow",
      description: "Complexities in tracking receivables, managing spend, and performing financial forecasting."
    },
    {
      stepNum: "05",
      icon: <SupportIcon className="h-5 w-5" />,
      title: "Insufficient Marketing",
      description: "Lack of cohesive digital branding and data-driven customer acquisition pipelines."
    }
  ];
  
  const procreativeSolutions = [
    {
      iconUrl: '/assets/icons/icon-nlp.svg',
      title: "MeetingMaster",
      description: "AI-powered meeting companion for bilingual transcription (English/Afrikaans), minutes, and task tracking.",
      href: "/solutions/meetingmaster",
      tier: "Silver"
    },
    {
      iconUrl: '/assets/icons/icon-analytics.svg',
      title: "AccountsEasy",
      description: "Automated financial co-pilot and bookkeeping assistant for SMMEs with receipt scanning and M365 integration.",
      href: "/solutions/accountseasy",
      tier: "Silver"
    },
    {
      iconUrl: '/assets/icons/icon-vision.svg',
      title: "TTriz",
      description: "Systematic innovation and patent analysis engine driven by the TRIZ engineering methodology.",
      href: "/solutions/ttriz",
      tier: "Gold"
    },
    {
      iconUrl: '/assets/icons/icon-automation.svg',
      title: "Stoccs",
      description: "Smart cloud inventory tracking, supplier management, and sales demand forecasting.",
      href: "/solutions/stoccs",
      tier: "Bronze"
    },
    {
      iconUrl: '/assets/icons/icon-strategy.svg',
      title: "ProCreative Projects",
      description: "Premium collaborative project management platform featuring Gantt charts and timelines.",
      href: "/solutions/projects-procreative",
      tier: "Bronze"
    },
    {
      iconUrl: '/assets/icons/icon-custom-dev.svg',
      title: "Social Maestro",
      description: "Social media generation, management, and campaign coordination tool.",
      href: "/solutions/maestro-procreative",
      tier: "Gold"
    },
  ];
  
  const processSteps = [
    {
      step: "01",
      title: "Discovery & Requirements",
      description: "Understanding your operational needs, assessing feasibility, and mapping target workflows."
    },
    {
      step: "02",
      title: "Solution Design & Customisation",
      description: "Architecting the solution design, selecting the tech stack, and customising templates."
    },
    {
      step: "03",
      title: "Integration & Cloud Deployment",
      description: "Implementing custom tools and platforms into your existing corporate workflows seamlessly."
    },
    {
      step: "04",
      title: "Continuous Support & Optimisation",
      description: "Providing ongoing monitoring, maintenance, and training for optimal team performance."
    }
  ];
  
  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden bg-background dark:bg-dark-background border-b border-border/30 dark:border-dark-border/30">
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="space-y-6"
          >
            <div className="h-8" />
            
            <h1 className="text-4xl sm:text-6xl md:text-7xl font-bold tracking-tight text-foreground dark:text-dark-foreground leading-[1.08] max-w-5xl mx-auto">
              Engineering <span className="font-serif italic text-accent font-light">bespoke software</span> for high-performance organizations.
            </h1>
            
            <p className="max-w-2xl mx-auto text-sm sm:text-base text-foreground/70 dark:text-[#E5E7EB]/70 leading-relaxed font-sans font-light">
              ProCreative designs, codes, and integrates custom software platforms to automate corporate workflows, solve operational bottlenecks, and drive measurable efficiency.
            </p>
            
            <div className="flex flex-wrap justify-center gap-4 pt-4">
              <Button href="/solutions" variant="accent" size="lg" className="rounded-full px-8 font-semibold tracking-wide text-xs uppercase">
                Explore Catalogue
              </Button>
              <Button href="/contact" variant="outline" size="lg" className="rounded-full px-8 font-semibold tracking-wide text-xs uppercase border-border hover:bg-secondary dark:border-dark-border dark:hover:bg-dark-secondary">
                Get In Touch
              </Button>
            </div>
          </motion.div>
          
          {/* Main platform preview video */}
          <motion.div 
            className="mt-16 relative mx-auto max-w-5xl rounded-2xl overflow-hidden border border-border/40 dark:border-dark-border/40 shadow-2xl bg-card dark:bg-dark-card"
            initial={{ opacity: 0, y: 50, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ delay: 0.3, duration: 1.0, ease: [0.16, 1, 0.3, 1] }}
          >
            <StrategicAIVideo className="w-full" />
          </motion.div>
        </div>
      </section>
      
      {/* Challenges Section */}
      <section className="py-20 bg-background dark:bg-dark-background">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-[10px] uppercase tracking-widest font-semibold text-accent font-heading">Operational Bottlenecks</span>
            <motion.h2 
              className="text-3xl sm:text-4xl font-bold text-foreground dark:text-dark-foreground mt-2"
              custom={0}
              variants={fadeInUpVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              Overcome Your Operational Challenges
            </motion.h2>
          </div>
          
          {/* Asymmetric Bento Grid */}
          <div className="grid gap-6 grid-cols-1 md:grid-cols-6 items-stretch">
            {challenges.map((challenge, index) => {
              // Map bento classes
              const spanClass = 
                index === 0 ? "md:col-span-4" :
                index === 1 ? "md:col-span-2" :
                index === 2 ? "md:col-span-2" :
                index === 3 ? "md:col-span-4" : "md:col-span-6";
                
              return (
                <motion.div
                  key={challenge.title}
                  custom={index + 1}
                  variants={fadeInUpVariants}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  className={`relative bg-card dark:bg-dark-card p-8 rounded-2xl border border-border/60 dark:border-dark-border/60 hover:border-accent/40 dark:hover:border-dark-accent/40 transition-all duration-300 hover:shadow-soft dark:hover:shadow-dark-card flex flex-col justify-between overflow-hidden group ${spanClass}`}
                >
                  {/* Elegant floating serif number */}
                  <span className="absolute top-4 right-6 font-serif italic text-4xl text-accent/15 dark:text-dark-accent/10 pointer-events-none select-none">
                    {challenge.stepNum}
                  </span>
                  
                  <div>
                    <div className="h-10 w-10 rounded-full bg-accent/5 dark:bg-dark-accent/5 flex items-center justify-center border border-accent/10 dark:border-dark-accent/10">
                      <div className="text-accent">
                        {challenge.icon}
                      </div>
                    </div>
                    <h3 className="mt-6 text-lg font-medium text-foreground dark:text-dark-foreground group-hover:text-accent dark:group-hover:text-dark-accent transition-colors">
                      {challenge.title}
                    </h3>
                  </div>
                  <p className="mt-3 text-sm text-foreground/60 dark:text-dark-foreground/60 leading-relaxed font-sans font-light">
                    {challenge.description}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>
      
      {/* Solutions Section */}
      <section className="py-20 bg-secondary/50 dark:bg-dark-secondary/20 border-t border-b border-border/30 dark:border-dark-border/30">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-[10px] uppercase tracking-widest font-semibold text-accent font-heading">Catalogue Showcase</span>
            <motion.h2 
              className="text-3xl sm:text-4xl font-bold text-foreground dark:text-dark-foreground mt-2"
              custom={0}
              variants={fadeInUpVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              Our Custom Software Platforms
            </motion.h2>
            <motion.p 
              className="mt-3 max-w-xl mx-auto text-sm text-foreground/60 dark:text-dark-foreground/60 font-light"
              custom={1}
              variants={fadeInUpVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              Fully featured software suites engineered by LMC ProCreative to automate and strengthen business workflows.
            </motion.p>
          </div>
          
          <div className="grid gap-6 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
            {procreativeSolutions.map((solution, index) => (
              <motion.div
                key={solution.title}
                custom={index + 2}
                variants={fadeInUpVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                onClick={() => window.location.href = solution.href}
                className="group flex flex-col justify-between bg-card dark:bg-dark-card p-8 rounded-2xl border border-border/60 dark:border-dark-border/60 hover:border-accent/40 dark:hover:border-dark-accent/40 transition-all duration-300 hover:shadow-soft dark:hover:shadow-dark-card cursor-pointer"
              >
                <div>
                  <div className="flex items-center justify-between">
                    <div className="h-10 w-10 rounded-full bg-accent/5 dark:bg-dark-accent/5 flex items-center justify-center border border-accent/10 dark:border-dark-accent/10">
                      <img 
                        src={solution.iconUrl} 
                        alt="" 
                        className="h-5 w-5"
                        aria-hidden="true" 
                      />
                    </div>
                    <span className="text-[9px] uppercase tracking-widest font-semibold border border-accent/20 dark:border-dark-accent/20 px-2.5 py-0.5 rounded-full text-accent">
                      {solution.tier}
                    </span>
                  </div>
                  <h3 className="mt-6 text-lg font-medium text-foreground dark:text-dark-foreground group-hover:text-accent dark:group-hover:text-dark-accent transition-colors">
                    {solution.title}
                  </h3>
                  <p className="mt-3 text-sm text-foreground/60 dark:text-dark-foreground/60 leading-relaxed font-sans font-light">
                    {solution.description}
                  </p>
                </div>
                <div className="mt-8 flex items-center gap-1.5 text-xs text-accent font-semibold tracking-wider uppercase opacity-0 group-hover:opacity-100 transition-opacity">
                  View Detail
                  <svg className="w-3.5 h-3.5 transform group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </motion.div>
            ))}
          </div>
          
          <div className="mt-12 text-center">
            <Button 
              href="/solutions" 
              variant="accent" 
              size="lg"
              className="rounded-full px-8 font-semibold tracking-wide text-xs uppercase"
            >
              View Full Catalogue
            </Button>
          </div>
        </div>
      </section>
      
      {/* Why ProCreative Section */}
      <section className="py-20 bg-background dark:bg-dark-background">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:grid lg:grid-cols-12 lg:gap-12 lg:items-center">
            {/* Text Content */}
            <div className="lg:col-span-6 space-y-6">
              <span className="text-[10px] uppercase tracking-widest font-semibold text-accent font-heading">Our Philosophy</span>
              <motion.h2
                className="text-3xl sm:text-4xl font-bold text-foreground dark:text-dark-foreground"
                custom={0}
                variants={fadeInUpVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
              >
                Technical Excellence, Designed for <span className="font-serif italic text-accent font-light">Longevity</span>
              </motion.h2>
              <motion.p
                className="text-sm text-foreground/75 dark:text-dark-foreground/75 leading-relaxed font-sans font-light"
                custom={1}
                variants={fadeInUpVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
              >
                We believe software should be a business asset, not a temporary patch. Just as the resilient King Protea flourishes in challenging conditions and adapts to stand strong, our custom platforms are engineered for longevity. No template clichés, no generic layouts—just robust, enduring systems designed to help South African enterprises thrive under pressure and grow with natural strength.
              </motion.p>
            </div>
 
            {/* King Protea Philosophy Video */}
            <div className="mt-10 lg:mt-0 lg:col-span-6">
              <motion.div
                className="relative mx-auto w-full aspect-video rounded-2xl shadow-xl overflow-hidden border border-border/40 dark:border-dark-border/40 bg-card dark:bg-dark-card"
                initial={{ opacity: 0, scale: 0.98 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.2, duration: 0.8 }}
                viewport={{ once: true }}
              >
                <video
                  className="w-full h-full object-cover"
                  src="/images/king-protea.mp4"
                  autoPlay
                  muted
                  loop
                  playsInline
                />
              </motion.div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Process Section */}
      <section className="py-20 bg-secondary/50 dark:bg-dark-secondary/20 border-t border-b border-border/30 dark:border-dark-border/30">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-[10px] uppercase tracking-widest font-semibold text-accent font-heading">Methodology</span>
            <motion.h2
              className="text-3xl sm:text-4xl font-bold text-foreground dark:text-dark-foreground mt-2"
              custom={0}
              variants={fadeInUpVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              Our Platform Implementation Process
            </motion.h2>
          </div>
 
          {/* Staggered Timeline Layout */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {processSteps.map((step, index) => (
              <motion.div
                key={step.step}
                custom={index + 2}
                variants={fadeInUpVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                className="space-y-4 relative"
              >
                <div className="flex items-baseline gap-2">
                  <span className="font-serif italic text-5xl text-accent font-light">
                    {step.step}
                  </span>
                  <div className="h-px bg-border/50 dark:bg-dark-border/50 flex-grow"></div>
                </div>
                <h4 className="text-base font-semibold text-foreground dark:text-dark-foreground">
                  {step.title}
                </h4>
                <p className="text-sm text-foreground/60 dark:text-dark-foreground/65 leading-relaxed font-sans font-light">
                  {step.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
 
      {/* Final CTA Section */}
      <section className="py-20 bg-[#0A1411] text-white text-center relative overflow-hidden">
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6">
          <h2 className="text-3xl sm:text-5xl font-bold tracking-tight">
            Ready to scale your <span className="font-serif italic text-accent font-light">operations</span>?
          </h2>
          <p className="max-w-xl mx-auto text-sm text-white/70 font-sans font-light">
            Assess our solutions catalogue or consult with our engineering team to map out custom automations.
          </p>
          <div className="flex flex-wrap justify-center gap-4 pt-4">
            <Button 
              href="/solutions" 
              variant="accent"
              size="lg"
              className="rounded-full px-8 font-semibold tracking-wide text-xs uppercase"
            >
              Explore Solutions
            </Button>
            <Button 
              href="/contact" 
              variant="outline" 
              size="lg"
              className="rounded-full px-8 font-semibold tracking-wide text-xs uppercase border-white/20 text-white hover:bg-white/10"
            >
              Contact Us
            </Button>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default HomePage;
